import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Styles/SettingsPage.css';
import '../constants/styleConstants.css';
import axiosClient from '../services/axios_client'; // Import your axios client

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();

  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const sanitize = (v: string) => v.replace(/\D/g, '').slice(0, 6);
  const isValidPin = (v: string) => /^\d{4,6}$/.test(v);

  const validate = () => {
    if (!isValidPin(currentPin)) return 'Current PIN must be 4–6 digits.';
    if (!isValidPin(newPin)) return 'New PIN must be 4–6 digits.';
    if (newPin === currentPin) return 'New PIN must be different from the current PIN.';
    if (confirmPin !== newPin) return 'The new PINs do not match.';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      const response = await axiosClient.post('/user/change-pin', {
        current_pin: currentPin,
        new_pin: newPin,
        new_pin_confirmation: confirmPin,
      });

      setSuccess(response.data.message || 'PIN changed successfully.');
      // Clear fields on success
      setCurrentPin('');
      setNewPin('');
      setConfirmPin('');

    } catch (err: any) {
      // Handle validation errors from Laravel (422)
      if (err.response && err.response.status === 422) {
        const validationErrors = err.response.data.errors;
        const firstError = Object.values(validationErrors)[0] as string[];
        setError(firstError[0] || 'Please check your input.');
      } else {
        // Handle other errors
        setError(err.response?.data?.message || 'An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-page">
      <header className="topbar">
        <button className="btn back-btn" onClick={() => navigate(-1)} aria-label="Go back">←</button>
        <div className="brand">
          <div className="brand-mark" aria-hidden="true">H</div>
          <h1 className="brand-title">Settings</h1>
        </div>
        <div aria-hidden="true" style={{ width: 40 }} />
      </header>

      <main className="settings-content">
        <section className="settings-card" aria-label="Change PIN">
          <h2 className="card-title">Change PIN</h2>
          <p className="card-subtitle">Use a 4–6 digit PIN for your account.</p>

          <form className="pin-form" onSubmit={handleSubmit} noValidate>
            <div className="field">
              <label htmlFor="currentPin">Current PIN</label>
              <div className="pin-group">
                <input
                  id="currentPin"
                  type={showCurrent ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="••••"
                  value={currentPin}
                  onChange={(e) => setCurrentPin(sanitize(e.target.value))}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setShowCurrent(s => !s)}
                  aria-label={showCurrent ? 'Hide current PIN' : 'Show current PIN'}
                >
                  {showCurrent ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="field">
              <label htmlFor="newPin">New PIN</label>
              <div className="pin-group">
                <input
                  id="newPin"
                  type={showNew ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="new-password"
                  placeholder="••••"
                  value={newPin}
                  onChange={(e) => setNewPin(sanitize(e.target.value))}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setShowNew(s => !s)}
                  aria-label={showNew ? 'Hide new PIN' : 'Show new PIN'}
                >
                  {showNew ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="field">
              <label htmlFor="confirmPin">Confirm new PIN</label>
              <div className="pin-group">
                <input
                  id="confirmPin"
                  type={showConfirm ? 'text' : 'password'}
                  inputMode="numeric"
                  autoComplete="new-password"
                  placeholder="••••"
                  value={confirmPin}
                  onChange={(e) => setConfirmPin(sanitize(e.target.value))}
                />
                <button
                  type="button"
                  className="icon-button"
                  onClick={() => setShowConfirm(s => !s)}
                  aria-label={showConfirm ? 'Hide confirm PIN' : 'Show confirm PIN'}
                >
                  {showConfirm ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {error && <div className="form-error" role="alert">{error}</div>}
            {success && <div className="form-success" role="status">{success}</div>}

            <div className="form-actions">
              <button className="btn" type="button" onClick={() => navigate(-1)}>Cancel</button>
              <button className="btn primary" type="submit" disabled={loading}>
                {loading ? 'Saving…' : 'Change PIN'}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
};

export default SettingsPage;